<!--
  GgSectionCard.vue

  @version 1.0.0
  @description Outlined section card with consistent header (title + optional suffix + widget slot),
               optional sub-header row, free-form content area, and a bottom CTA button.
               Designed for detail-page sections (Anagrafica, Intervento, Materiali, etc.).

  @props
    title       (string, required) — Main heading, left-aligned
    titleSuffix (string, optional) — Secondary text next to the title (e.g. "(2)")
    ctaText     (string, default "Modifica") — CTA button label
    hideCta     (boolean, default false) — Hides the footer entirely
    noPadding   (boolean, default false) — Removes padding from the content area
    loading     (boolean, default false) — Loading state on CTA button
    disabled    (boolean, default false) — Disabled state on CTA button

  @slots
    #header-widget   — Right side of header row (icons, badges, summary text)
    #header-extra    — Additional row below the header (e.g. tabs, toggles)
    #default         — Main content area
    #footer          — Full override of the footer (replaces the default CTA)

  @emits
    cta-click — Fired when the default CTA button is clicked

  @usage
  <GgSectionCard title="Anagrafica" @cta-click="openEdit">
    <template #header-widget>
      <gg-btn variant="text" size="small">VEDI MAPPA</gg-btn>
    </template>
    <p>Contenuto sezione...</p>
  </GgSectionCard>

  <GgSectionCard title="Materiali" title-suffix="(2)" cta-text="Aggiungi Materiali" @cta-click="addMaterial">
    <MyMaterialsTable :items="materials" />
  </GgSectionCard>

  <GgSectionCard title="Rapportino relativo a" @cta-click="edit">
    <template #header-extra>
      <v-btn-toggle mandatory>
        <v-btn>Macchina specifica</v-btn>
        <v-btn>Tutta la commessa</v-btn>
      </v-btn-toggle>
    </template>
    <p>Dettagli macchina...</p>
  </GgSectionCard>

  <GgSectionCard title="Report CST" hide-cta>
    <template #header-widget>
      <gg-btn variant="text" size="small">FATTURA</gg-btn>
    </template>
    <p>Riepilogo costi...</p>
  </GgSectionCard>
-->

<template>
  <section class="gg-section-card">
    <!-- ─── Header ──────────────────────────────────────────────────────── -->
    <header class="gg-section-card__header">
      <div class="gg-section-card__title-row">
        <div class="gg-section-card__title-group">
          <v-icon v-if="icon" size="20" class="gg-section-card__icon">ms:{{ icon }}</v-icon>
          <h3 class="gg-section-card__title">
            {{ title }}
          </h3>
          <span
            v-if="titleSuffix"
            class="gg-section-card__suffix"
          >
            {{ titleSuffix }}
          </span>
        </div>

        <div
          v-if="$slots['header-widget']"
          class="gg-section-card__widget"
        >
          <slot name="header-widget" />
        </div>
      </div>

      <!-- Optional sub-header row (tabs, toggles, etc.) -->
      <div
        v-if="$slots['header-extra']"
        class="gg-section-card__header-extra"
      >
        <slot name="header-extra" />
      </div>
    </header>

    <!-- ─── Content ─────────────────────────────────────────────────────── -->
    <div
      class="gg-section-card__content"
      :class="{ 'gg-section-card__content--no-padding': noPadding }"
    >
      <slot />
    </div>

    <!-- ─── Footer ──────────────────────────────────────────────────────── -->
    <footer
      v-if="!hideCta || $slots.footer"
      class="gg-section-card__footer"
    >
      <slot name="footer">
        <gg-btn
          variant="text"
          color="primary"
          :loading="loading"
          :disabled="disabled"
          @click="handleCtaClick"
        >
          {{ ctaText }}
        </gg-btn>
      </slot>
    </footer>
  </section>
</template>

<script setup lang="ts">
/**
 * GgSectionCard — Outlined section card for detail pages
 *
 * Provides a consistent card layout with header (title + widget),
 * optional sub-header, content area, and CTA footer.
 */

defineOptions({
  name: 'GgSectionCard',
})

// =============================================================================
// Props
// =============================================================================

interface Props {
  /** Main heading text, left-aligned */
  title: string
  /** Secondary text next to the title (e.g. count "(2)") */
  titleSuffix?: string
  /** CTA button label */
  ctaText?: string
  /** Hides the footer entirely (unless #footer slot is used) */
  hideCta?: boolean
  /** Removes padding from the content area */
  noPadding?: boolean
  /** Loading state on CTA button */
  loading?: boolean
  /** Disabled state on CTA button */
  disabled?: boolean
  /** Material Symbol icon name shown left of title */
  icon?: string
}

withDefaults(defineProps<Props>(), {
  titleSuffix: undefined,
  ctaText: 'Modifica',
  hideCta: false,
  noPadding: false,
  loading: false,
  disabled: false,
  icon: undefined,
})

// =============================================================================
// Emits
// =============================================================================

const emit = defineEmits<{
  /** Fired when the default CTA button is clicked */
  'cta-click': []
}>()

// =============================================================================
// Handlers
// =============================================================================

function handleCtaClick(): void {
  emit('cta-click')
}
</script>

<style lang="scss" scoped>
@use '@/themes/variables' as *;

.gg-section-card {
  border: 1px solid rgb(var(--v-theme-outline));
  border-radius: $border-radius;
  background-color: rgb(var(--v-theme-surface-bright));
  overflow: hidden;

  // ─── Header ──────────────────────────────────────────────────────────

  &__header {
    padding: $spacing-md;
    min-height: 40px;
    border-bottom: 1px solid rgb(var(--v-theme-outline));
  }

  &__title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $spacing-sm;
  }

  &__title-group {
    display: flex;
    align-items: center;
    gap: $spacing-sm;
    min-width: 0;
  }

  &__icon {
    flex-shrink: 0;
    color: rgb(var(--v-theme-on-surface));
  }

  &__title {
    font-size: $font-size-md;
    font-weight: $font-weight-semibold;
    color: rgb(var(--v-theme-on-surface));
    line-height: $line-height-tight;
    margin: 0;
  }

  &__suffix {
    font-size: $font-size-base;
    font-weight: $font-weight-normal;
    color: rgb(var(--v-theme-on-surface-variant));
    white-space: nowrap;
  }

  &__widget {
    display: flex;
    align-items: center;
    gap: $spacing-xs;
    flex-shrink: 0;
  }

  // ─── Header extra (tabs, toggles) ────────────────────────────────────

  &__header-extra {
    padding-top: $spacing-sm;
    border-top: 1px solid rgb(var(--v-theme-outline-variant));
  }

  // ─── Content ─────────────────────────────────────────────────────────

  &__content {
    padding: $spacing-md;

    &--no-padding {
      padding: 0;
    }
  }

  // ─── Mobile: tighter spacing ─────────────────────────────────────────
  @media (max-width: $breakpoint-sm) {
    &__header { padding: $spacing-sm $spacing-sm; }
    &__content { padding: $spacing-sm; }
    &__content--no-padding { padding: 0; }
    &__footer { padding: $spacing-sm; }
  }

  // ─── Footer ──────────────────────────────────────────────────────────

  &__footer {
    padding: $spacing-md;
    border-top: 1px solid rgb(var(--v-theme-outline-variant));
  }
}
</style>
